import axios from 'axios'

/**
 * Agent API — talks to the Edaptio Laravel API for the in-process agent.
 *
 * The Cloudflare-Worker era used a JWT mint + WebSocket round-trip via
 * agent-stg.edaptio.com / agent.edaptio.com. That has been replaced by
 * a single SSE endpoint on the API itself (`POST /agent/chat`) — the
 * agent runs in the same process, so user scope flows directly from
 * the existing bearer token. No more session tokens, no more WebSocket.
 *
 * Conversation history endpoints (list / load / archive) are unchanged
 * shape-wise; the underlying schema moved to UUID conversation ids
 * (`agent_conversations.id` is now a varchar(36)).
 */
export default {
    async listConversations() {
        return axios.get('/agent/conversations')
    },

    async loadConversation(conversationId) {
        return axios.get(`/agent/conversations/${conversationId}/messages`)
    },

    async archiveConversation(conversationId) {
        return axios.post(`/agent/conversations/${conversationId}/archive`)
    },

    /**
     * POST /agent/chat with the user's message and (optionally) the current
     * conversation_id. Returns a Fetch Response whose body is an
     * `EventSource`-style SSE stream of Vercel AI SDK protocol frames
     * (text-delta, tool-input-available, tool-output-available, finish, …).
     *
     * The conversation_id for a fresh session is returned via the
     * `X-Conversation-Id` response header, so the client can persist it
     * before reading the body.
     *
     * Caller is responsible for reading the body via response.body
     * .getReader() and parsing `data: …` lines. See chatStream consumption
     * in agent.store.js.
     */
    async chatStream({ message, conversationId, context, signal }) {
        const baseUrl = (import.meta.env.VITE_BASE_API_URL || '').replace(/\/$/, '')
        const url = `${baseUrl}/agent/chat`
        const token = localStorage.getItem('id_token') || ''
        const body = JSON.stringify({
            message,
            conversation_id: conversationId || null,
            context: context || null,
        })
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Authorization': `Bearer ${token}`,
            },
            body,
            signal,
        })
    },
}
